/**
 * PDF PREVIEW PANE — CMD, 02-11-2026.
 *
 * A draft document (work order, purchase order) opens beside the page instead
 * of in a new browser tab. Shared by both modules since 02-11-2026.
 *   Large screens (lg+)  a pane slides in from the right; the page narrows to
 *                        make room (useSplitWidth), so both stay usable.
 *   Phones and tablets   a full-screen sheet slides up from the bottom.
 *
 * WHY THE PAGES ARE DRAWN WITH PDF.JS, NOT AN <iframe>
 * Phone browsers do not show a PDF inside a page: Android Chrome offers a
 * download instead, iOS Safari shows the first page only. pdf.js draws every
 * page onto a canvas the width of the pane, the same on every device, and is
 * loaded only when a preview is opened, so it costs nothing otherwise.
 * isEvalSupported is off: the documents are our own, but it closes the one
 * known hole in this pdf.js version (CVE-2024-4367) regardless.
 */
import { useEffect, useRef, useState } from 'react'
import { Download, ExternalLink, Minus, Plus, X } from 'lucide-react'
import { Loading } from '@/components/ui'

/** Width of the pane on large screens; the page gives up the same. */
export const PANE_WIDTH = 'clamp(420px, 46vw, 880px)'

export interface PdfPreview {
  blob: Blob
  fileName: string
  title: string
  /** The warning under the title. Defaults to the work order's wording. */
  note?: string
}

/** True while a pane is open on a large screen - for the page to narrow. */
export function useSplitWidth(open: boolean) {
  const [wide, setWide] = useState(() => window.matchMedia('(min-width: 1024px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const on = () => setWide(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return open && wide
}

export function PdfPreviewPane({ preview, onClose }: {
  preview: PdfPreview | null
  onClose: () => void
}) {
  // Kept mounted through the closing animation, then dropped.
  const [shown, setShown] = useState<PdfPreview | null>(preview)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (preview) {
      setShown(preview)
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const t = setTimeout(() => setShown(null), 320)
    return () => clearTimeout(t)
  }, [preview])

  useEffect(() => {
    if (!preview) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    // A full-screen sheet on a phone must not scroll the page underneath.
    const small = !window.matchMedia('(min-width: 1024px)').matches
    if (small) document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [preview, onClose])

  if (!shown) return null

  return (
    <>
      {/* Dim the page behind the sheet on small screens only. */}
      <div onClick={onClose} aria-hidden
           className={`fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-300 lg:hidden
                       ${visible ? 'opacity-100' : 'pointer-events-none opacity-0'}`} />
      <aside role="dialog" aria-modal="false" aria-label={shown.title}
             style={{ ['--pane-w' as string]: PANE_WIDTH }}
             className={`fixed inset-x-0 bottom-0 top-0 z-50 flex flex-col bg-slate-100 shadow-2xl
                         transition-transform duration-300 ease-out motion-reduce:transition-none
                         lg:left-auto lg:w-[var(--pane-w)] lg:border-l lg:border-slate-200
                         ${visible ? 'translate-y-0 lg:translate-x-0' : 'translate-y-full lg:translate-x-full lg:translate-y-0'}`}>
        <Toolbar preview={shown} onClose={onClose} />
        <Pages blob={shown.blob} />
      </aside>
    </>
  )
}

function Toolbar({ preview, onClose }: { preview: PdfPreview; onClose: () => void }) {
  const url = useObjectUrl(preview.blob)
  return (
    <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800">{preview.title}</p>
        <p className="truncate text-[11px] text-amber-700">
          {preview.note ?? 'Draft for review - not a work order, not for issue'}
        </p>
      </div>
      {url && (
        <>
          <a href={url} download={preview.fileName} className="btn-ghost btn-sm" title="Download this preview">
            <Download className="h-4 w-4" /><span className="hidden sm:inline">Download</span>
          </a>
          <a href={url} target="_blank" rel="noopener" className="btn-ghost btn-sm hidden sm:inline-flex"
             title="Open in the browser's own PDF viewer (to print)">
            <ExternalLink className="h-4 w-4" /><span className="hidden md:inline">Open</span>
          </a>
        </>
      )}
      <button className="btn-ghost btn-sm" onClick={onClose} title="Close (Esc)" aria-label="Close preview">
        <X className="h-5 w-5" />
      </button>
    </header>
  )
}

function useObjectUrl(blob: Blob) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    const u = URL.createObjectURL(blob)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [blob])
  return url
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PdfDoc = any

function Pages({ blob }: { blob: Blob }) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [doc, setDoc] = useState<PdfDoc | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [width, setWidth] = useState(0)
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    let live = true
    let loaded: PdfDoc | null = null
    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist')
        const worker = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default
        pdfjs.GlobalWorkerOptions.workerSrc = worker
        const data = new Uint8Array(await blob.arrayBuffer())
        loaded = await pdfjs.getDocument({ data, isEvalSupported: false }).promise
        if (live) setDoc(loaded)
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'The preview could not be drawn.')
      }
    })()
    return () => { live = false; loaded?.destroy?.() }
  }, [blob])

  // Fit the pages to the pane, and redraw when it changes size.
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const pages = doc ? Array.from({ length: doc.numPages as number }, (_, i) => i + 1) : []

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={boxRef} className="h-full overflow-auto px-2 py-3 sm:px-4">
        {error ? (
          <p className="m-4 rounded-lg bg-white p-4 text-sm text-red-700">{error} Use Download instead.</p>
        ) : !doc || width === 0 ? (
          <Loading label="Drawing the preview…" />
        ) : (
          <div className="mx-auto space-y-3" style={{ width: Math.max(280, (width - 16) * zoom) }}>
            {pages.map((n) => (
              <PageCanvas key={n} doc={doc} pageNo={n} width={Math.max(280, (width - 16) * zoom)} />
            ))}
            <p className="pb-2 text-center text-[11px] text-slate-500">{pages.length} page{pages.length === 1 ? '' : 's'}</p>
          </div>
        )}
      </div>
      {doc && (
        <div className="absolute bottom-4 right-4 flex items-center gap-1 rounded-full border border-slate-200 bg-white/95 px-1 py-1 shadow">
          <button className="btn-ghost btn-sm rounded-full" aria-label="Zoom out"
                  disabled={zoom <= 0.6} onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(1)))}>
            <Minus className="h-4 w-4" />
          </button>
          <button className="min-w-[3rem] text-xs font-medium text-slate-600" onClick={() => setZoom(1)} title="Fit to width">
            {Math.round(zoom * 100)}%
          </button>
          <button className="btn-ghost btn-sm rounded-full" aria-label="Zoom in"
                  disabled={zoom >= 2.4} onClick={() => setZoom((z) => Math.min(2.4, +(z + 0.2).toFixed(1)))}>
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

function PageCanvas({ doc, pageNo, width }: { doc: PdfDoc; pageNo: number; width: number }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [ratio, setRatio] = useState(1.414)

  useEffect(() => {
    let live = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let task: any = null
    // A short pause so dragging the window does not redraw on every pixel.
    const t = setTimeout(async () => {
      const page = await doc.getPage(pageNo)
      if (!live || !ref.current) return
      const base = page.getViewport({ scale: 1 })
      setRatio(base.height / base.width)
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5)
      const vp = page.getViewport({ scale: (width / base.width) * dpr })
      const canvas = ref.current
      canvas.width = Math.floor(vp.width)
      canvas.height = Math.floor(vp.height)
      task = page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp })
      try { await task.promise } catch { /* superseded by a newer draw */ }
    }, 120)
    return () => { live = false; clearTimeout(t); task?.cancel?.() }
  }, [doc, pageNo, width])

  return (
    <canvas ref={ref} className="block rounded bg-white shadow"
            style={{ width, height: width * ratio }} aria-label={`Page ${pageNo}`} />
  )
}
